import { genericErrors } from "./errorUtils.mjs"

/**
 * Calculates the present value of a single future payment discounted by a given interest rate and period.
 *
 * @param {number} money - The amount of money to be received in the future.
 * @param {number} interest - The interest (discount) rate per period (as a decimal).
 * @param {number} n - The number of periods until the payment is received.
 * @returns {number} The present value of the future payment.
 *
 * @example
 * PVCalc(1000, 0.05, 3)
 * // Calculates the present value of $1000 to be received in 3 periods at 5% interest rate.
 */
function PVCalc(money, interest, n) {
  return money / Math.pow(1 + interest, n)
}

/**
 * Calculates the future value of an investment based on periodic payments, interest rate, and number of periods
 * (as Excel standard amortizing loans).
 *
 * @param {number} rate - The interest rate per period (as a decimal, e.g., 0.05 for 5%).
 * @param {number} nper - The total number of payment periods.
 * @param {number} pmt - The payment made each period (negative for outgoing payments).
 * @param {number} [pv=0] - The present value or initial lump sum (negative for outgoing).
 * @param {boolean|number} [type=0] - Indicates when payments are due: 0 for end of period, 1 for beginning.
 * @returns {number} The future value of the investment.
 *
 * @example
 * FV(0.05, 10, -100, 0, 0)
 * // Calculates the future value with 5% rate, 10 periods, payments of 100 at end of period, no initial amount.
 *
 * @example
 * FV(0.05, 10, -100, -1000, 1)
 * // Calculates future value with payments at beginning of each period and an initial amount of 1000.
 */
function FV(rate, nper, pmt, pv, type) {
  const term = Math.pow(1 + rate, nper)
  type = type ? 1 : 0
  if (!pv) pv = 0
  const fv = -((pmt * (1 + rate * type) * (term - 1)) / rate + pv * term)
  return fv
}

/**
 * Calculates the present value of an investment based on future value, periodic payments, interest rate, and number of periods.
 *
 * @param {number} rate - The interest rate per period (as a decimal, e.g., 0.05 for 5%).
 * @param {number} nper - The total number of payment periods.
 * @param {number} pmt - The payment made each period (negative for outgoing payments).
 * @param {number} [fv=0] - The future value or cash balance desired after the last payment.
 * @param {boolean|number} [type=0] - Indicates when payments are due: 0 for end of period, 1 for beginning.
 * @returns {number} The present value of the investment.
 *
 * @example
 * PV(0.05, 10, -100, 0, 0)
 * // Calculates present value with 5% rate, 10 periods, payments of 100 at end of period, no future value.
 *
 * @example
 * PV(0.05, 10, -100, 1000, 1)
 * // Calculates present value with payments at beginning of each period and a future value of 1000.
 */
function PV(rate, nper, pmt, fv, type) {
  const term = Math.pow(1 + rate, nper)
  type = type ? 1 : 0
  if (!fv) fv = 0
  const pv = (fv - (pmt * (1 + rate * type) * (term - 1)) / rate) / term
  return pv
}

/**
 * Calculates the Net Present Value (NPV) of a series of future payments given a discount rate.
 *
 * - Discounts each payment to its present value using the `PVCalc` function.
 * - Sums all discounted payments to return the net present value.
 *
 * @param {number} rate - The discount rate per period (as a decimal, e.g., 0.05 for 5%).
 * @param {number[]} payments - An array of future payment amounts.
 * @returns {number} The net present value of the payments.
 *
 * @example
 * NPV(0.05, [-1000, 300, 400, 500])
 * // Calculates the NPV for four payments with a 5% discount rate.
 */
function NPV(rate, payments) {
  return payments.reduce((acc, payment, index) => acc + PVCalc(payment, rate, index + 1), 0)
}

/**
 * Calculates the number of periods for an investment/loan.
 *
 * Example usage:
 * // Monthly 6% annual interest, €300 payment, €10 000 loan
 * const periods = NPER(0.06 / 12, -300, 10000); // ~36.73
 *
 * @param {number} rate - Interest rate per period (decimal, e.g., 0.05 for 5%)
 * @param {number} pmt - Payment per period (should be negative for cash outflow)
 * @param {number} pv - Present value of the loan/investment
 * @param {number} [fv=0] - Future value (default is 0)
 * @param {number} [type=0] - When payments are made: 0 = end of period, 1 = beginning
 * @returns {number} Number of periods
 */
export function NPER(rate, pmt, pv, fv = 0, type = 0) {
  if (rate === 0) return -(pv + fv) / pmt // Simple case with zero interest

  type = type ? 1 : 0

  return Math.log((pmt * (1 + rate * type) - fv * rate) / (pv * rate + pmt * (1 + rate * type))) / Math.log(1 + rate)
}

/**
 * Calculates the interest portion of a payment for a given period in an annuity.
 *
 * @param {number} rate - The interest rate per period (as a decimal, e.g., 0.05 for 5%).
 * @param {number} per - The payment period number (1-based).
 * @param {number} nper - The total number of payment periods.
 * @param {number} pv - The present value or principal amount (negative for outgoing).
 * @param {number} fv - The future value or cash balance desired after last payment.
 * @param {boolean|number} type - When payments are due: 0 for end of period, 1 for beginning.
 * @returns {number} The interest portion of the payment for the given period.
 *
 * @example
 * IPMT(0.05, 1, 10, -1000, 0, 0)
 * // Calculates interest portion of the first payment for a 10-period loan with 5% interest.
 *
 * @example
 * IPMT(0.05, 5, 10, -1000, 0, 1)
 * // Calculates interest portion of the fifth payment when payments are due at beginning of period.
 */
function IPMT(rate, per /* 1 based */, nper, pv, fv, type) {
  type = type ? 1 : 0
  if (rate === 0) return 0
  const pmt = PMT(rate, nper, pv, fv, type)
  let ipmt = FV(rate, per - 1, pmt, pv, type) * rate
  if (type === 1) ipmt /= 1 + rate
  return ipmt
}

/**
 * Calculates the principal portion of a payment for a given period in an annuity.
 *
 * @param {number} rate - The interest rate per period (as a decimal, e.g., 0.05 for 5%).
 * @param {number} per - The payment period number (1-based).
 * @param {number} nper - The total number of payment periods.
 * @param {number} pv - The present value or principal amount (negative for outgoing).
 * @param {number} fv - The future value or cash balance desired after last payment.
 * @param {boolean|number} type - When payments are due: 0 for end of period, 1 for beginning.
 * @returns {number} The principal portion of the payment for the given period.
 *
 * @example
 * PPMT(0.05, 1, 10, -1000, 0, 0)
 * // Calculates principal portion of the first payment for a 10-period loan with 5% interest.
 *
 * @example
 * PPMT(0.05, 5, 10, -1000, 0, 1)
 * // Calculates principal portion of the fifth payment when payments are due at beginning of period.
 */
function PPMT(rate, per /* 1 based */, nper, pv, fv, type) {
  return PMT(rate, nper, pv, fv, type) - IPMT(rate, per, nper, pv, fv, type)
}

/**
 * Calculates the payment amount per period for a loan or investment based on interest rate, number of periods, present value, and future value.
 *
 * @param {number} rate - The interest rate per period (as a decimal, e.g., 0.05 for 5%).
 * @param {number} nper - The total number of payment periods.
 * @param {number} pv - The present value or principal amount (negative for outgoing).
 * @param {number} [fv=0] - The future value or cash balance desired after last payment.
 * @param {boolean|number} [type=0] - When payments are due: 0 for end of period, 1 for beginning.
 * @returns {number} The payment amount per period.
 *
 * @example
 * PMT(0.05, 10, -1000, 0, 0)
 * // Calculates payment amount for a loan with 5% interest, 10 periods, $1000 principal, payments at end of period.
 *
 * @example
 * PMT(0, 10, -1000, 0)
 * // Calculates payment amount with zero interest rate (simple division).
 */
function PMT(rate, nper, pv, fv, type = 0) {
  type = type ? 1 : 0
  if (!fv) fv = 0
  if (rate === 0) {
    const pmt = (-pv - fv) / nper
    return pmt
  } else {
    const term = Math.pow(1 + rate, nper)
    let pmt = (-pv - fv / term) / ((1 - 1 / term) / rate) // http://www.tvmcalcs.com/index.php/calculators/apps/lease_payments
    if (type === 1) {
      pmt = pmt / (1 + rate)
    }
    return pmt
  }
}

/**
 * Calculates the payment for a loan or investment with payments in advance (multiple payments at start).
 * When nb is 1, this is equivalent to the standard PMT function.
 * Note: If nb > 1, the last nb payments will be zero.
 *
 * @param {number} rate - The interest rate per period (as a decimal, e.g., 0.0075 for 0.75%).
 * @param {number} nper - The total number of payment periods.
 * @param {number} pv - The present value or principal amount (negative for outgoing).
 * @param {number} fv - The future value or cash balance desired after last payment.
 * @param {number} nb - Number of payments made in advance at the beginning.
 * @returns {number} The payment amount per period.
 *
 * @example
 * // Equivalent to PMT when nb=1
 * PMTWithPaymentsInAdvance(0.0075, 24, -3500, 1000, 1)
 *
 * @example
 * // With 3 payments in advance; last payments will be zero
 * PMTWithPaymentsInAdvance(0.0075, 24, -3500, 1000, 3)
 */
export function PMTWithPaymentsInAdvance(rate, nper, pv, fv, nb) {
  if (!fv) fv = 0
  if (!nb) nb = 0
  if (rate === 0) {
    const pmt = (-pv - fv) / (nper + nb - 1)
    return pmt
  } else {
    const term = Math.pow(1 + rate, nper)
    const term2 = Math.pow(1 + rate, nper - nb)
    const pmt = (-pv - fv / term) / ((1 - 1 / term2) / rate + nb) // http://www.tvmcalcs.com/index.php/calculators/apps/lease_payments
    return pmt
  }
}

/**
 * Calculates the interest portion of a payment for a linear loan (term loan) at a specific period.
 *
 * @param {number} rate - The interest rate per period (as a decimal).
 * @param {number} per - The payment period number (1-based).
 * @param {number} nper - The total number of payment periods.
 * @param {number} pv - The present value or principal amount (negative for outgoing).
 * @returns {number} The interest payment amount for the given period.
 *
 * @example
 * linearLoanIPMT(0.05, 1, 10, -1000) // Interest portion for the first period
 * linearLoanIPMT(0.05, 5, 10, -1000) // Interest portion for the fifth period
 */
function linearLoanIPMT(rate, per, nper, pv) {
  const ppmt = linearLoanPPMT(rate, nper, pv)
  const ipmt = (-pv - ppmt * (per - 1)) * rate
  return ipmt
}

/**
 * Calculates the principal portion of each payment for a linear loan with equal principal repayments.
 *
 * @param {number} rate - The interest rate per period (not used in calculation here but typically relevant).
 * @param {number} nper - The total number of payment periods.
 * @param {number} pv - The present value or principal amount (negative for outgoing).
 * @returns {number} The fixed principal repayment amount per period.
 *
 * @example
 * linearLoanPPMT(0.05, 10, -1000)
 * // Returns -100, meaning each payment includes $100 principal repayment.
 */
function linearLoanPPMT(rate, nper, pv) {
  const ppmt = -pv / nper
  return ppmt
}

/**
 * Calculates the total payment (principal + interest) for a linear loan at a specific period.
 *
 * @param {number} rate - The interest rate per period (as a decimal).
 * @param {number} per - The payment period number (1-based).
 * @param {number} nper - The total number of payment periods.
 * @param {number} pv - The present value or principal amount (negative for outgoing).
 * @returns {number} The total payment amount for the given period.
 *
 * @example
 * linearLoanPMT(0.05, 1, 10, -1000) // Total payment for the first period
 * linearLoanPMT(0.05, 5, 10, -1000) // Total payment for the fifth period
 */
function linearLoanPMT(rate, per, nper, pv) {
  return linearLoanPPMT(rate, nper, pv) + linearLoanIPMT(rate, per, nper, pv)
}

/**
 * Returns a weighting function for loan payments based on provided weights.
 *
 * @param {number} nper - The total number of payment periods.
 * @param {Array<number>|function|null} weights - An array of weights, a weighting function, or null/undefined.
 * @returns {function} A function that takes an index and returns the corresponding weight.
 *
 * @throws {Error} Throws an error if weights is not an array, function, or falsy value.
 *
 * @example
 * // Using array weights
 * const weightFn = _getFunctionweight(4, [0.4, 0.3, 0.2, 0.1])
 * weightFn(0) // 0.4
 * weightFn(3) // 0.1
 *
 * @example
 * // Using a function
 * const customWeightFn = index => 1 / (index + 1)
 * const weightFn2 = _getFunctionweight(4, customWeightFn)
 * weightFn2(0) // 1
 * weightFn2(1) // 0.5
 *
 * @example
 * // No weights provided (equal weighting)
 * const equalWeightFn = _getFunctionweight(4, null)
 * equalWeightFn(0) // 0.25
 * equalWeightFn(3) // 0.25
 */
function _getFunctionweight(nper, weights) {
  if (Array.isArray(weights)) {
    return i => weights[i] || 0
  } else if (typeof weights === "function") {
    return weights
  } else if (!weights) {
    return () => 1 / nper
  }
  throw Error(genericErrors.INVALID_ARRAY_OR_FUNCTION_FIELD + "'weights'")
}

/**
 * Calculates the interest portion of a payment for a weighted linear loan.
 *
 * @param {number} rate - The periodic interest rate (e.g., monthly rate).
 * @param {number} per - The payment period number (1-based index).
 * @param {number} nper - Total number of payment periods.
 * @param {number} pv - Present value (principal) of the loan (usually negative).
 * @param {Array<number>|function|null} weights - Array of weights, weighting function, or null for equal weights.
 * @returns {number} The interest portion of the payment for the specified period.
 *
 * @example
 * // Using array weights for a 4-period loan
 * weightedLinearLoanIPMT(0.05, 2, 4, -1000, [0.4, 0.3, 0.2, 0.1])
 *
 * @example
 * // Using equal weights (no weights provided)
 * weightedLinearLoanIPMT(0.05, 2, 4, -1000, null)
 */
function weightedLinearLoanIPMT(rate, per, nper, pv, weights) {
  const weightFunction = _getFunctionweight(nper, weights)
  let sumweight = 0
  let totalweight = 0
  for (let i = 0; i < nper; i++) {
    totalweight += weightFunction(i)
    if (i < per - 1) sumweight += weightFunction(i)
  }
  const sumppmt = (-pv * sumweight) / totalweight
  const ipmt = (-pv - sumppmt) * rate
  return ipmt
}

/**
 * Calculates the principal portion of a payment for a weighted linear loan.
 *
 * @param {number} rate - The periodic interest rate (unused here but kept for API consistency).
 * @param {number} per - The payment period number (1-based index).
 * @param {number} nper - Total number of payment periods.
 * @param {number} pv - Present value (principal) of the loan (usually negative).
 * @param {Array<number>|function|null} weights - Array of weights, weighting function, or null for equal weights.
 * @returns {number} The principal portion of the payment for the specified period.
 *
 * @example
 * weightedLinearLoanPPMT(0.05, 2, 4, -1000, [0.4, 0.3, 0.2, 0.1]) // weighted principal for period 2
 *
 * @example
 * weightedLinearLoanPPMT(0.05, 2, 4, -1000, null) // equal weights by default
 */
function weightedLinearLoanPPMT(rate, per, nper, pv, weights) {
  const weightFunction = _getFunctionweight(nper, weights)
  let totalweight = 0
  for (let i = 0; i < nper; i++) {
    totalweight += weightFunction(i)
  }
  const ppmt = (-pv * weightFunction(per - 1)) / totalweight
  return ppmt
}

/**
 * Calculates the total payment (principal + interest) for a weighted linear loan at a given period.
 *
 * @param {number} rate - The periodic interest rate.
 * @param {number} per - The payment period number (1-based).
 * @param {number} nper - Total number of payment periods.
 * @param {number} pv - Present value (principal) of the loan (usually negative).
 * @param {Array<number>|function|null} weights - Array of weights, weighting function, or null for equal weights.
 * @returns {number} The total payment amount for the specified period.
 *
 * @example
 * weightedLinearLoanPMT(0.05, 2, 4, -1000, [0.4, 0.3, 0.2, 0.1])
 */
function weightedLinearLoanPMT(rate, per, nper, pv, weights) {
  return weightedLinearLoanPPMT(rate, per, nper, pv, weights) + weightedLinearLoanIPMT(rate, per, nper, pv, weights)
}

/**
 * Calculates constant weighted instalments for a loan or investment based on given weights, interest rate,
 * number of periods, present value, future value, and payment type.
 * Uses an iterative approach to find instalments such that the internal rate of return (IRR) matches the given rate.
 *
 * @param {number[]} weights - Array of weights representing the relative proportions of each instalment.
 * @param {number} rate - The interest rate per period (e.g., 0.05 for 5%).
 * @param {number} nper - Total number of payment periods.
 * @param {number} pv - Present value, or initial amount of the loan/investment.
 * @param {number} [fv=0] - Future value or cash balance desired after the last payment. Defaults to 0.
 * @param {number} [type=0] - When payments are due: 0 = end of period, 1 = beginning of period.
 * @returns {number[]} Array of calculated weighted instalment amounts for each period.
 *
 * @example
 * // Equal weights, payments at end of period
 * const instalments = solveConstantInstallement([1, 1, 1, 1], 0.05, 4, -1000);
 *
 * @example
 * // Unequal weights, payments at beginning of period, with future value
 * const instalments = solveConstantInstallement([40, 30, 20, 10], 0.07, 4, -1500, 100, 1);
 */
function solveConstantInstallement(weights, rate, nper, pv, fv = 0, type = 0) {
  const nweights = [...weights]
  let sum = 0
  for (let i = 0; i < nweights.length; i++) sum += nweights[i]
  for (let i = 0; i < nweights.length; i++) nweights[i] *= nper / sum // normalize

  const pmt = PMT(rate, nper, pv, fv)
  let min = 0
  let max = pmt * 10
  let guess
  let lastGuess = max
  let iter = 0
  //console.log("xxx", sum, nbNotnull, min, max, ...nweights)

  const guessTimesItem = item => guess * item

  for (;;) {
    guess = (min + max) / 2
    if (iter > 1 && Math.abs(lastGuess - guess) < 0.0000000000000000001) break
    lastGuess = guess

    // compute cashflows
    const cashflows = []
    if (!type) cashflows.push(pv)
    cashflows.push(...nweights.map(guessTimesItem))
    if (type) cashflows[0] += pv
    if (type) cashflows.push(fv)
    else cashflows[cashflows.length - 1] += fv

    const irr = IRR(cashflows)

    //console.log("N°" + iter, min.toFixed(13), "<", guess.toFixed(13), "<", max.toFixed(13), irr, ...(cashflows.map(i => i.toFixed(2))))

    if (irr < rate) min = guess
    else max = guess

    if (isNaN(Math.abs(irr - rate)) || Math.abs(irr - rate) < 0.0000000000000000001) break

    iter++
  }
  console.log("Iter instalments", iter)
  const weightedinstalments = nweights.map(item => guess * item)
  return weightedinstalments
}

/**
 * Calculates constant weighted instalments for a loan or investment given a fixed first payment,
 * interest rate, number of periods, present value, future value, and payment type.
 * It uses an iterative approach to adjust subsequent instalments so that the net present value (NPV) of cash flows equals zero.
 *
 * @param {number} first - The amount of the first payment.
 * @param {number} rate - The interest rate per period (e.g., 0.05 for 5%).
 * @param {number} nper - Total number of payment periods.
 * @param {number} pv - Present value or initial amount of the loan/investment.
 * @param {number} [fv=0] - Future value or cash balance desired after the last payment. Defaults to 0.
 * @param {number} [type=0] - When payments are due: 0 = end of period, 1 = beginning of period.
 * @returns {number} The calculated constant instalment amount for all payments after the first.
 *
 * @example
 * // Calculate instalments with a first payment of 100, payments at end of period
 * const instalment = solveConstantInstallementWithFirstPayment(100, 0.05, 5, -1000);
 *
 * @example
 * // Calculate instalments with first payment of 200, payments at beginning of period, and future value of 50
 * const instalment = solveConstantInstallementWithFirstPayment(200, 0.06, 6, -1500, 50, 1);
 */
function solveConstantInstallementWithFirstPayment(first, rate, nper, pv, fv = 0, type = 0) {
  const pmt = PMT(rate, nper, pv - first, fv)
  let min = 0
  let max = pmt * 10
  let guess
  let lastGuess = max
  let iter = 0

  for (;;) {
    guess = (min + max) / 2
    if (iter > 1 && Math.abs(lastGuess - guess) < 0.0000000000000000001) break
    lastGuess = guess

    // compute cashflows
    const cashflows = []
    if (!type) cashflows.push(pv)
    cashflows.push(first)
    for (let i = 1; i < nper; i++) cashflows.push(guess)
    if (type) cashflows[0] += pv
    if (type) cashflows.push(fv)
    else cashflows[cashflows.length - 1] += fv

    let npv = 0
    cashflows.forEach((cashflow, i) => (npv += PVCalc(cashflow, rate, cashflows.length - i)))

    if (npv < 0) min = guess
    else max = guess

    if (Math.abs(npv) < 0.0000000000000000001) break

    iter++
  }
  console.log("Iter instalments", iter, guess)
  return guess
}

/**
 * Calculates the interest portion of a payment in a weighted constant loan for a specific period.
 *
 * @param {number} rate - The interest rate per period (e.g., 0.05 for 5%).
 * @param {number} per - The payment period number (1-based index).
 * @param {number} nper - Total number of payment periods.
 * @param {number} pv - Present value or principal amount of the loan.
 * @param {number[]} weightedinstalments - Array of weighted instalment amounts for each period.
 * @returns {number} The interest payment amount for the given period.
 *
 * @example
 * const interestPayment = weightedConstantLoanIPMT(0.05, 3, 5, 1000, [200, 200, 200, 200, 200]);
 */
function weightedConstantLoanIPMT(rate, per, nper, pv, weightedinstalments) {
  let sum = 0
  for (let i = 0; i < per - 1; i++) {
    sum += weightedinstalments[i]
  }
  const ipmt = (-pv - sum) * rate
  return ipmt
}

/**
 * Calculates the principal portion of a payment in a weighted constant loan for a specific period.
 *
 * @param {number} rate - The interest rate per period (e.g., 0.05 for 5%).
 * @param {number} per - The payment period number (1-based index).
 * @param {number} nper - Total number of payment periods.
 * @param {number} pv - Present value or principal amount of the loan.
 * @param {number[]} weightedinstalments - Array of weighted instalment amounts for each period.
 * @returns {number} The principal payment amount for the given period.
 *
 * @example
 * const principalPayment = weightedConstantLoanPPMT(0.05, 3, 5, 1000, [200, 200, 200, 200, 200]);
 */
function weightedConstantLoanPPMT(rate, per, nper, pv, weightedinstalments) {
  const ppmt = weightedinstalments[per - 1] - weightedConstantLoanIPMT(rate, per, nper, pv, weightedinstalments)
  return ppmt
}

/**
 * Calculates the Internal Rate of Return (IRR) for a series of cash flows.
 *
 * This function simulates Excel's RATE behavior for cash flow analysis.
 * If the first cash flow is positive, all cash flow signs are inverted to align with Excel's convention.
 * Uses a binary search method to approximate the IRR that sets the Net Present Value (NPV) to zero.
 *
 * @param {number[]} CArray - Array of cash flows where each element represents a cash flow at a time period.
 *                            Positive values represent cash inflows, negative values represent outflows.
 * @returns {number} The internal rate of return (IRR) as a decimal (e.g., 0.0177 for ~1.77%).
 *
 * @example
 * // First cash flow negative, subsequent positive returns expected IRR close to 1.77%
 * IRR([-1000, 110, 110, 110, 110, 110, 110, 110, 110, 110, 110])
 * // Returns approximately 0.0177 (1.77%)
 *
 * @example
 * // First cash flow positive, signs inverted internally, same IRR result as above
 * IRR([1000, -110, -110, -110, -110, -110, -110, -110, -110, -110, -110])
 * // Returns approximately 0.0177 (1.77%)
 */
function IRR(CArray) {
  // this is an attempt to simulate Excel behavior e.g:
  // =RATE(10;110;-1000) => 1,77154269%
  // =RATE(10;-110;1000) => 1,77154269% so same value
  // therefore if first cashflow is positive, change all signs
  if (CArray && CArray[0] && CArray[0] > 0) CArray = CArray.map(it => -it)

  let min = -1.0
  let max = 1.0
  let guess
  let lastGuess = max
  let notSame = true
  let NPV = 0
  do {
    NPV = 0
    guess = (min + max) / 2
    if (Math.abs(lastGuess - guess) < 0.0000000000000000001) notSame = false
    lastGuess = guess
    for (let j = 0; j < CArray.length; j++) {
      NPV += PVCalc(CArray[j], guess, j)
    }
    if (NPV > 0) {
      min = guess
    } else {
      max = guess
    }
  } while (notSame && Math.abs(NPV) > 0.0000000000000000001)
  return guess
}

/**
 * Calculates the interest rate per period of an annuity based on the number of periods, payment, present value, future value, and timing.
 *
 * Constructs cash flows based on the parameters and computes the rate using the IRR function.
 *
 * @param {number} nper - Number of payment periods.
 * @param {number} pmt - Payment amount per period.
 * @param {number} pv - Present value or initial amount.
 * @param {number} [fv=0] - Future value or cash balance desired after the last payment.
 * @param {boolean} [type=false] - When true, payments are due at the beginning of the period; otherwise, at the end.
 *
 * @returns {number} The interest rate per period as a decimal.
 *
 * @example
 * // Payments made at the end of each period
 * RATE(10, 110, -1000)
 * // Returns the interest rate per period
 *
 * @example
 * // Payments made at the beginning of each period with future value
 * RATE(10, 110, -1000, 500, true)
 * // Returns the interest rate per period considering payments due at start and a future value
 */
function RATE(nper, pmt, pv, fv, type) {
  fv = fv || 0
  const cashflows = []
  if (!type) cashflows.push(pv)
  for (let i = 0; i < nper; i++) cashflows.push(pmt)
  if (type) cashflows[0] += pv
  if (type) cashflows.push(fv)
  else cashflows[cashflows.length - 1] += fv
  const irr = IRR(cashflows)
  return irr
}

const xirrErrors = {
  MISSING_OR_INVALID_CASHFLOWS_AND_DATES: "MISSING_OR_INVALID_CASHFLOWS_AND_DATES|Cashflows and dates arrays are invalid|",
  INVALID_TRANSACTIONS_SAME_DAY: "INVALID_TRANSACTIONS_SAME_DAY|Transactions must not all be on the same day|",
  INVALID_TRANSACTIONS_ALL_NEGATIVE: "INVALID_TRANSACTIONS_ALL_NEGATIVE|Transactions must not all be positive|",
  INVALID_TRANSACTIONS_ALL_POSITIVE: "INVALID_TRANSACTIONS_ALL_POSITIVE|Transactions must not all be negative|",
  NEWTON_RAPHSON_FAILED_TO_CONVERGE: "NEWTON_RAPHSON_FAILED_TO_CONVERGE|Newton-Raphson algorithm did not converge|",
}

/**
 * Calculates the Extended Internal Rate of Return (XIRR) for irregular cash flows occurring at specific dates.
 * https://github.com/RayDeCampo/nodejs-xirr
 *
 * Uses the Newton-Raphson method to iteratively find the rate of return that sets the net present value (NPV) of
 * the cash flows to zero, accounting for the exact timing of each cash flow.
 *
 * @param {number[]} cashflows - Array of cash flow amounts, where negative values represent outflows and positive values inflows.
 * @param {number[]} dates - Array of dates corresponding to each cash flow in milliseconds since the Unix epoch.
 * @param {number} [guess] - Optional initial guess for the rate, as a decimal (e.g., 0.1 for 10%).
 *
 * @returns {number} The annualized internal rate of return as a decimal.
 *
 * @throws {Error} Throws error if inputs are invalid or if the Newton-Raphson method fails to converge.
 *
 * @throws {Error} MISSING_OR_INVALID_CASHFLOWS_AND_DATES - If cashflows and dates arrays are missing, empty, or lengths differ.
 * @throws {Error} INVALID_TRANSACTIONS_SAME_DAY - If all transactions occur on the same day.
 * @throws {Error} INVALID_TRANSACTIONS_ALL_NEGATIVE - If all cash flows are positive (no negative transactions).
 * @throws {Error} INVALID_TRANSACTIONS_ALL_POSITIVE - If all cash flows are negative (no positive transactions).
 * @throws {Error} NEWTON_RAPHSON_FAILED_TO_CONVERGE - If the Newton-Raphson algorithm does not converge.
 *
 * @example
 * // Calculate XIRR for cashflows on irregular dates
 * const cashflows = [-10000, 2750, 4250, 3250, 2750]
 * const dates = [
 *   new Date("2018-01-15").getTime(),
 *   new Date("2018-04-15").getTime(),
 *   new Date("2018-10-15").getTime(),
 *   new Date("2019-02-15").getTime(),
 *   new Date("2019-04-15").getTime()
 * ]
 * XIRR(cashflows, dates)
 * // Returns the annualized IRR as a decimal, e.g., 0.0596 (~5.96%)
 *
 * @example
 * // With an initial guess provided
 * XIRR(cashflows, dates, 0.1)
 */
function XIRR(cashflows, dates, guess) {
  if (!cashflows || !dates || !cashflows.length || cashflows.length !== dates.length) {
    throw Error(xirrErrors.MISSING_OR_INVALID_CASHFLOWS_AND_DATES)
  }

  // https://github.com/scijs/newton-raphson-method
  function newtonRaphson(f, fp, x0) {
    let x1, y, yp, yph, ymh, yp2h, ym2h
    const h = 1e-4
    let iter = 0
    while (iter++ < 20) {
      // Compute the value of the function
      y = f(x0)
      if (fp) {
        yp = fp(x0)
      } else {
        // Needs numerical derivatives
        yph = f(x0 + h)
        ymh = f(x0 - h)
        yp2h = f(x0 + 2 * h)
        ym2h = f(x0 - 2 * h)
        yp = ((ym2h - yp2h + 8 * (yph - ymh)) * (1 / h)) / 12
      }
      // Check for badly conditioned update (extremely small first deriv relative to function)
      if (Math.abs(yp) <= 2.220446049250313e-16 * Math.abs(y)) {
        return false
      }
      // Update the guess
      x1 = x0 - y / yp
      // Check for convergence
      if (Math.abs(x1 - x0) <= 1e-7 * Math.abs(x1)) {
        return x1
      }
      // Transfer update to the new guess
      x0 = x1
    }
    return false
  }

  const MILLIS_PER_DAY = 1000 * 60 * 60 * 24
  const DAYS_IN_YEAR = 365
  const investments = []
  let start = Math.floor(dates[0] / MILLIS_PER_DAY)
  let end = start
  let minAmount = Number.POSITIVE_INFINITY
  let maxAmount = Number.NEGATIVE_INFINITY
  let total = 0
  let deposits = 0
  for (let i = 0; i < cashflows.length; i++) {
    total += cashflows[i]
    if (cashflows[i] < 0) {
      deposits += -cashflows[i]
    }
    const epochDays = Math.floor(dates[i] / MILLIS_PER_DAY)
    start = Math.min(start, epochDays)
    end = Math.max(end, epochDays)
    minAmount = Math.min(minAmount, cashflows[i])
    maxAmount = Math.max(maxAmount, cashflows[i])
    investments.push({
      amount: cashflows[i],
      epochDays: epochDays,
    })
  }

  if (start === end) {
    throw Error(xirrErrors.INVALID_TRANSACTIONS_SAME_DAY)
  }

  if (minAmount >= 0) {
    throw Error(xirrErrors.INVALID_TRANSACTIONS_ALL_NEGATIVE)
  }

  if (maxAmount < 0) {
    throw Error(xirrErrors.INVALID_TRANSACTIONS_ALL_POSITIVE)
  }

  investments.forEach(function (investment) {
    // Number of years (including fraction) this item applies
    investment.years = (end - investment.epochDays) / DAYS_IN_YEAR
  })

  const days = end - start
  if (maxAmount === 0) {
    return -1
  }

  function value(rate) {
    return investments.reduce(function (sum, investment) {
      // Make the vars more Math-y, makes the derivative easier to see
      const A = investment.amount
      const Y = investment.years
      if (-1 < rate) {
        return sum + A * Math.pow(1 + rate, Y)
      } else if (rate < -1) {
        // Extend the function into the range where the rate is less
        // than -100%.  Even though this does not make practical sense,
        // it allows the algorithm to converge in the cases where the
        // candidate values enter this range

        // We cannot use the same formula as before, since the base of
        // the exponent (1+rate) is negative, this yields imaginary
        // values for fractional years.
        // E.g. if rate=-1.5 and years=.5, it would be (-.5)^.5,
        // i.e. the square root of negative one half.

        // Ensure the values are always negative so there can never
        // be a zero (as long as some amount is non-zero).
        // This formula also ensures that the derivative is positive
        // (when rate < -1) so that Newton's method is encouraged to
        // move the candidate values towards the proper range

        return sum - Math.abs(A) * Math.pow(-1 - rate, Y)
      } else if (Y === 0) {
        return sum + A // Treat 0^0 as 1
      } else {
        return sum
      }
    }, 0)
  }

  function derivative(rate) {
    return investments.reduce(function (sum, investment) {
      // Make the vars more Math-y, makes the derivative easier to see
      const A = investment.amount
      const Y = investment.years
      if (Y === 0) {
        return sum
      } else if (-1 < rate) {
        return sum + A * Y * Math.pow(1 + rate, Y - 1)
      } else if (rate < -1) {
        return sum + Math.abs(A) * Y * Math.pow(-1 - rate, Y - 1)
      } else {
        return sum
      }
    }, 0)
  }

  if (guess && isNaN(guess)) {
    throw Error(genericErrors.INVALID_NUMBER_FIELD + "'guess'")
  }

  if (!guess) {
    guess = total / deposits / (days / DAYS_IN_YEAR)
  }

  const rate = newtonRaphson(value, derivative, guess)
  if (rate === false) {
    // truthiness strikes again, !rate is true when rate is zero
    throw Error(xirrErrors.NEWTON_RAPHSON_FAILED_TO_CONVERGE)
  }

  return rate
}

/**
 * Calculates the nominal annual interest rate given the effective interest rate and the number of compounding periods per year.
 *
 * This replicates Excel's NOMINAL function behavior.
 * https://support.microsoft.com/en-us/office/nominal-function-7f1ae29b-6b92-435e-b950-ad8b190ddd2b
 *
 * @param {number} effectiveRate - The effective interest rate (as a decimal, e.g., 0.12 for 12%).
 * @param {number} nbPerYear - The number of compounding periods per year.
 * @returns {number} The nominal annual interest rate as a decimal.
 *
 * @example
 * // Calculate nominal rate for an effective annual rate of 12% compounded monthly
 * NOMINAL(0.12, 12)
 * // Returns approximately 0.113 (11.3%)
 */

function NOMINAL(effectiveRate, nbPerYear) {
  return ((1 + effectiveRate) ** (1 / nbPerYear) - 1) * nbPerYear
}

/**
 * Calculates the effective annual interest rate given the nominal interest rate and the number of compounding periods per year.
 *
 * This replicates Excel's EFFECT function behavior.
 * https://support.microsoft.com/en-us/office/effect-function-910d4e4c-79e2-4009-95e6-507e04f11bc4
 *
 * @param {number} nominalRate - The nominal annual interest rate (as a decimal, e.g., 0.12 for 12%).
 * @param {number} nbPerYear - The number of compounding periods per year.
 * @returns {number} The effective annual interest rate as a decimal.
 *
 * @example
 * // Calculate effective rate for a nominal annual rate of 12% compounded monthly
 * EFFECT(0.12, 12)
 * // Returns approximately 0.1268 (12.68%)
 */
function EFFECT(nominalRate, nbPerYear) {
  return (1 + nominalRate / nbPerYear) ** nbPerYear - 1
}

/**
 * Rounds a number to a specified number of decimal places, mimicking Excel's ROUND function behavior.
 * https://support.microsoft.com/en-us/office/round-function-c018c5d8-40fb-4053-90b1-b3e7f61a213c
 *
 * Addresses JavaScript floating-point precision issues by adding a small epsilon before rounding.
 * Defaults to rounding to 2 decimal places if decimals parameter is omitted.
 *
 * Explanation: the rule is
 *   0.0049 rounded at 2 decimals => 0.00
 *   0.005  rounded at 2 decimals => 0.01
 *
 * But javascript way of handling float numbers makes 0.0049999999999999999 like 0.05
 * However Math.round(x / 100) * 100 will return 0
 *
 * In order to avoid the issue we add a small 0.0000001 to make sur we will round correctly:
 *
 * ```js
 * const x1 = 0.004999999999999999 // one less "9"
 * const math1 = Math.round(x1 * 100) / 100
 * const fina1 = finance.ROUND(x1, 2)
 * console.log(x1, math1, fina1, math1 === fina1) // true
 *
 * const x2 = 0.0049999999999999999 // one more "9"
 * const math2 = Math.round(x2 * 100) / 100
 * const fina2 = finance.ROUND(x2, 2)
 * console.log(x2, math2, fina2, math2 === fina2) // false
 * ```
 *
 * @param {number} number - The number to round.
 * @param {number} [decimals=2] - The number of decimal places to round to. Defaults to 2.
 * @returns {number} The rounded number.
 *
 * @example
 * // Round 0.0049 to 2 decimals results in 0.00
 * ROUND(0.0049) // returns 0.00
 *
 * @example
 * // Round 0.005 to 2 decimals results in 0.01
 * ROUND(0.005) // returns 0.01
 *
 * @example
 * // Round a negative number with 3 decimals
 * ROUND(-1.23456, 3) // returns -1.235
 */
function ROUND(number, decimals = 2) {
  if (!number) return number
  const ratio = decimals === 2 ? 100 : Math.pow(10, decimals)
  const isNegative = number < 0
  const roundedNumber = Math.round(Math.abs(number) * ratio + 0.00001) / ratio
  return isNegative ? roundedNumber * -1 : roundedNumber
}

/**
 * Rounds a number down to a specified number of decimal places.
 *
 * Uses a small epsilon to address floating-point precision issues.
 * Defaults to rounding down to 2 decimal places if decimals parameter is omitted.
 *
 * @param {number} number - The number to round down.
 * @param {number} [decimals=2] - The number of decimal places to round down to. Defaults to 2.
 * @returns {number} The rounded down number.
 *
 * @example
 * // Round down 0.009 to 2 decimals results in 0.00
 * ROUNDDOWN(0.009) // returns 0.00
 *
 * @example
 * // Round down 1.239 with 2 decimals results in 1.23
 * ROUNDDOWN(1.239, 2) // returns 1.23
 */
function ROUNDDOWN(number, decimals = 2) {
  if (!number) return number
  const ratio = decimals === 2 ? 100 : Math.pow(10, decimals)
  // Added 0.00001 to fix decimal problem in floating point par example 299.9 * 100 = 29989.999999999996
  return Math.floor(number * ratio + +0.00001) / ratio
}

/**
 * Rounds a number up to a specified number of decimal places.
 *
 * Defaults to rounding up to 2 decimal places if decimals parameter is omitted.
 *
 * @param {number} number - The number to round up.
 * @param {number} [decimals=2] - The number of decimal places to round up to. Defaults to 2.
 * @returns {number} The rounded up number.
 *
 * @example
 * // Round up 0.001 to 2 decimals results in 0.01
 * ROUNDUP(0.001) // returns 0.01
 *
 * @example
 * // Round up 1.231 with 2 decimals results in 1.24
 * ROUNDUP(1.231, 2) // returns 1.24
 */
function ROUNDUP(number, decimals = 2) {
  if (!number) return number
  const ratio = decimals === 2 ? 100 : Math.pow(10, decimals)
  return Math.ceil(number * ratio) / ratio
}

/**
 * Truncates a number to a specified number of decimal places.
 *
 * Uses a small epsilon to handle floating-point precision issues.
 * Defaults to truncating to 2 decimal places if decimals parameter is omitted.
 *
 * @param {number} number - The number to truncate.
 * @param {number} [decimals=2] - The number of decimal places to keep. Defaults to 2.
 * @returns {number} The truncated number.
 *
 * @example
 * // Truncate 1.239 to 2 decimals results in 1.23
 * TRUNC(1.239) // returns 1.23
 *
 * @example
 * // Truncate -1.239 with 3 decimals results in -1.239
 * TRUNC(-1.239, 3) // returns -1.239
 */
function TRUNC(number, decimals = 2) {
  if (!number) return number
  const ratio = decimals === 2 ? 100 : Math.pow(10, decimals)
  // Added 0.00001 to fix decimal problem in floating point par example 299.9 * 100 = 29989.999999999996
  return Math.trunc(number * ratio + +0.00001) / ratio
}

/**
 * Converts a nominal yearly interest rate to an actuarial (periodic) rate.
 *
 * ⚠️ This function should be deprecated because it does not match Excel's behavior.
 *
 * @param {number} annualRate - The nominal annual interest rate (as a decimal).
 * @param {number} nbPerYear - The number of compounding periods per year.
 * @returns {number} The periodic interest rate for one compounding period.
 *
 * @example
 * // Convert nominal annual rate of 12% to monthly periodic rate
 * nominalYearlyToActuarial(0.12, 12) // returns approximately 0.00948879 (~0.9489%)
 */
function nominalYearlyToActuarial(annualRate, nbPerYear) {
  //nbPerYear : nb periode per year ( Monthly = 12 Quarterly 4 yearly 1 )
  return (1 + annualRate) ** (1 / nbPerYear) - 1
}

/**
 * Converts an actuarial (periodic) interest rate to a nominal yearly rate.
 *
 * ⚠️ This function should be deprecated because it does not match Excel's behavior.
 *
 * @param {number} actuarialRate - The periodic interest rate for one compounding period (as a decimal).
 * @param {number} nbPerYear - The number of compounding periods per year.
 * @returns {number} The nominal annual interest rate.
 *
 * @example
 * // Convert monthly periodic rate of ~0.9489% to nominal annual rate
 * actuarialToNominalYearly(0.00948879, 12) // returns approximately 0.12 (12%)
 */
function actuarialToNominalYearly(actuarialRate, nbPerYear) {
  //nbPerYear : nb periode per year ( Monthly = 12 Quarterly 4 yearly 1 )
  return (1 + actuarialRate) ** nbPerYear - 1
}

/**
 * Computes a detailed payment plan (amortization schedule) based on loan parameters.
 *
 * @param {(Date|string)} startDate - The start date of the payment plan. Can be a Date object or a date string.
 * @param {string} [frequency="30"] - Payment frequency in days as a string. Supported values:
 *    - "30": Monthly payments (default),
 *    - "90": Quarterly payments,
 *    - "365": Yearly payments.
 * @param {number} rate - Interest rate per period (as a decimal).
 * @param {number} nper - Number of payment periods.
 * @param {number} pv - Present value or loan amount.
 * @param {number} [fv=0] - Future value, default is 0.
 * @param {number} [type=0] - Payment timing: 0 for end of period, 1 for beginning.
 * @returns {object} An object containing the input parameters and an array `plan` with detailed payment info per period.
 *
 * Each item in the `plan` array has the following structure:
 * - index: Payment period number (starting at 1).
 * - date: Date object of the payment date (if `startDate` provided).
 * - principal: Principal portion of the payment.
 * - interest: Interest portion of the payment.
 * - payment: Total payment amount.
 *
 * @example
 * // Monthly payments starting Jan 1, 2025, for 12 periods:
 * computePaymentPlan("2025-01-01", "30", 0.05 / 12, 12, 10000)
 *
 * @example
 * // Quarterly payments starting Jan 1, 2025, for 4 periods:
 * computePaymentPlan(new Date(2025, 0, 1), "90", 0.05 / 4, 4, 10000)
 */
function computePaymentPlan(startDate, frequency = "30", rate, nper, pv, fv = 0, type = 0) {
  type = type ? 1 : 0
  const pmt = PMT(rate, nper, pv, fv, type)
  const pp = {
    params: {
      rate,
      nper,
      pv,
      fv,
      type,
      pmt,
    },
    plan: [],
  }
  for (let i = 1; i <= nper; i++) {
    const ipmt = IPMT(rate, i, nper, pv, fv, type)
    const ppmt = pmt - ipmt
    const dt = startDate ? new Date(startDate) : undefined
    if (frequency === "90") {
      if (dt) dt.setMonth(dt.getMonth() + 3 * (i - type))
    } else if (frequency === "365") {
      if (dt) dt.setMonth(dt.getMonth() + 12 * (i - type))
    } else {
      if (dt) dt.setMonth(dt.getMonth() + i - type)
    }
    const item = {
      index: i,
      date: dt ? dt : undefined,
      principal: ppmt,
      interest: ipmt,
      payment: pmt,
    }
    pp.plan.push(item)
  }
  return pp
}

const goalSeekErrors = {
  NO_ACCEPTABLE_RESULT: "NO_ACCEPTABLE_RESULT|Could not find acceptable result.",
}

/**
 * Attempts to find a value that makes the provided increasing function approach a target value using a binary search (goal seek) method.
 *
 * WARNING: This function requires that the input function `func` is monotonically increasing; it will not work correctly with decreasing functions.
 *
 * @param {function} func - The function to evaluate, which must accept three arguments:
 *   - current guess (Number),
 *   - additional function arguments (any),
 *   - a boolean indicating if this is the final call (true/false).
 *   It should return an array where the first element is the function value at the guess, and the second element is optional output data.
 * @param {Number} targetValue - The target value to reach with the function's output.
 * @param {*} funcArgs - Additional arguments passed to the function `func`.
 * @param {Number} [initialValue] - Optional initial guess for the search, influences the search range.
 * @throws {Error} Throws an error with code "NO_ACCEPTABLE_RESULT" if no acceptable solution is found within 300 iterations.
 * @returns {[Number, any]} Returns a tuple where the first element is the found value that approaches the target, and the second element is optional output data returned from `func`.
 *
 * @example
 * // Example function that increases linearly and returns value and data
 * function exampleFunc(x, args, finalCall) {
 *   return [2 * x + args.offset, {final: finalCall}];
 * }
 * const [result, data] = GOALSEEK(exampleFunc, 10, {offset: 0}, 10);
 * // result should be approximately 5, data.final will be true
 */

function GOALSEEK(func, targetValue, funcArgs, initialValue) {
  let min = 0
  let max = 10_000_000
  if (initialValue) {
    min = 0
    max = initialValue * 2
  } else if (initialValue === 0) {
    min = -1
    max = 1
  }
  let prevY = 0
  for (let i = 0; i < 300; i++) {
    const guess = (min + max) / 2
    const y = func(guess, funcArgs, false)[0]
    if (targetValue < y) {
      max = guess
    } else {
      min = guess
    }
    let diff = y - prevY
    if (diff < 0) diff = -diff
    if (diff < 0.000001) {
      const outputData = func(guess, funcArgs, true)[1]
      return [guess, outputData]
    }
    prevY = y
  }
  throw Error(goalSeekErrors.NO_ACCEPTABLE_RESULT)
}

export {
  actuarialToNominalYearly,
  computePaymentPlan,
  EFFECT,
  FV,
  GOALSEEK,
  IPMT,
  IRR,
  linearLoanIPMT,
  linearLoanPMT,
  linearLoanPPMT,
  NOMINAL,
  nominalYearlyToActuarial,
  NPV,
  PMT,
  PPMT,
  PV,
  RATE,
  ROUND,
  ROUNDDOWN,
  ROUNDUP,
  solveConstantInstallement,
  solveConstantInstallementWithFirstPayment,
  TRUNC,
  weightedConstantLoanIPMT,
  weightedConstantLoanPPMT,
  weightedLinearLoanIPMT,
  weightedLinearLoanPMT,
  weightedLinearLoanPPMT,
  XIRR,
}
